import { autoLayoutElementForViewport, layoutMobilePageElements } from "./escapeRoomViewportAutoLayout";

const DESKTOP_VIEWPORT = { width: 960, height: 540 };

/** Mismos tamaños que `VIEWPORTS` en el portal (móvil 9 : 19.5, retrato). */
export const ER_VIEWPORTS = [
	{ id: "desktop", width: 960, height: 540 },
	{ id: "tablet", width: 768, height: 540 },
	{ id: "mobile", width: 360, height: 780 },
];

export function getErViewportDimensions(viewportId) {
	const v = ER_VIEWPORTS.find((x) => x.id === viewportId);
	return v || ER_VIEWPORTS[0];
}

/** Aproxima el viewport del editor según el tamaño de pantalla (al abrir “Reproducir” usa el tab activo; aquí lo inferimos). */
export function pickErViewportFromSize(innerWidth, innerHeight) {
	const w = Number(innerWidth) || 960;
	const h = Number(innerHeight) || 540;
	const shortSide = Math.min(w, h);
	if (shortSide <= 480) return "mobile";
	if (shortSide <= 860) return "tablet";
	return "desktop";
}

const LAYOUT_IDS = new Set(["row", "columns", "grid"]);

/**
 * Disposición de opciones del quiz según tamaño (editor / juego).
 * @param {object} firstQ - questions[0]
 * @param {'desktop'|'tablet'|'mobile'} viewportId
 */
export function getQuizOptionsLayoutForBreakpoint(firstQ, viewportId) {
	const base = LAYOUT_IDS.has(firstQ?.optionsLayout) ? firstQ.optionsLayout : "row";
	if (viewportId === "tablet") {
		const t = firstQ?.optionsLayoutTablet;
		if (typeof t === "string" && t.trim() && LAYOUT_IDS.has(t)) return t;
		return base;
	}
	if (viewportId === "mobile") {
		const m = firstQ?.optionsLayoutMobile;
		if (typeof m === "string" && m.trim() && LAYOUT_IDS.has(m)) return m;
		const t = firstQ?.optionsLayoutTablet;
		if (typeof t === "string" && t.trim() && LAYOUT_IDS.has(t)) return t;
		return base;
	}
	return base;
}

function scaleFontSizeForViewport(val, scale) {
	if (val == null) return val;
	if (typeof val === "number" && Number.isFinite(val)) {
		return Math.max(8, Math.round(val * scale));
	}
	const s = String(val).trim();
	const m = /^(\d+(?:\.\d+)?)px$/i.exec(s);
	if (m) return `${Math.max(8, Math.round(parseFloat(m[1]) * scale))}px`;
	return val;
}

/** Escala tipografía y radios cuando el elemento se adapta por factor (sin breakpoint manual). */
export function applyTypographyScaleToElement(el, scale) {
	if (!el || scale >= 0.999) return el;
	const next = { ...el };
	if (el.style && typeof el.style === "object") {
		const st = { ...el.style };
		if (st.fontSize != null) st.fontSize = scaleFontSizeForViewport(st.fontSize, scale);
		if (st.borderRadiusPercent == null && typeof st.borderRadius === "number") {
			st.borderRadius = Math.max(0, Math.round(st.borderRadius * scale));
		}
		next.style = st;
	}
	if (el.type === "quiz" && el.content && typeof el.content === "object") {
		const c = { ...el.content };
		if (c.quizOptionsStyle && typeof c.quizOptionsStyle === "object") {
			const qos = { ...c.quizOptionsStyle };
			if (qos.fontSize != null) qos.fontSize = scaleFontSizeForViewport(qos.fontSize, scale);
			c.quizOptionsStyle = qos;
		}
		next.content = c;
	}
	return next;
}

/** Fusiona posición/estilo guardados en breakpoints para un viewport (null si no hay override manual). */
export function mergeElementForViewport(el, viewportId) {
	const override = el?.breakpoints?.[viewportId];
	if (!override || typeof override.x !== "number") return null;
	const merged = {
		...el,
		x: override.x,
		y: override.y ?? el.y,
		width: override.width ?? el.width,
		height: override.height ?? el.height,
	};
	if (override.content) merged.content = { ...(el.content || {}), ...override.content };
	if (override.style) merged.style = { ...(el.style || {}), ...override.style };
	return merged;
}

/** Obtiene elementos para un viewport (desktop = base; tablet/mobile = breakpoints o escala). Alinea con el backoffice. */
export function getElementsForViewport(page, viewportId = "desktop") {
	const baseElements = page?.elements || [];
	if (viewportId === "desktop") return baseElements;
	const vp = ER_VIEWPORTS.find((v) => v.id === viewportId);
	if (!vp) return baseElements;
	const scaleX = vp.width / DESKTOP_VIEWPORT.width;
	const scaleY = vp.height / DESKTOP_VIEWPORT.height;
	const scale = Math.min(scaleX, scaleY);
	if (viewportId === "mobile") {
		const autoLaid = layoutMobilePageElements(baseElements, vp, (el, s) =>
			applyTypographyScaleToElement(el, s ?? scale)
		);
		const autoByKey = new Map();
		autoLaid.forEach((laid, index) => {
			const key = laid.elementId || `__idx_${index}`;
			autoByKey.set(key, laid);
			if (laid.elementId) autoByKey.set(laid.elementId, laid);
		});
		return baseElements.map((el, index) => {
			const manual = mergeElementForViewport(el, "mobile");
			if (manual) return manual;
			const key = el.elementId || `__idx_${index}`;
			return autoByKey.get(key) || autoByKey.get(el.elementId) || el;
		});
	}
	return baseElements.map((el) => {
		const manual = mergeElementForViewport(el, viewportId);
		if (manual) return manual;
		const laid = autoLayoutElementForViewport(el, viewportId);
		return applyTypographyScaleToElement(
			{
				...el,
				x: laid.x,
				y: laid.y,
				width: laid.width,
				height: laid.height,
			},
			laid.scale ?? scale
		);
	});
}

export function getPlayablePages(pages) {
	if (!pages || !Array.isArray(pages)) return [];
	// Respetar el orden que envía backoffice tal cual (array order).
	// En algunos juegos legacy el campo `order` puede quedar desincronizado.
	return [...pages];
}
